import {
	useSearchParams,
	useNavigate
} from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { useState } from 'react';
import { useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
	addAmendment,
	addStudy,
	fetchStudies,
	getStudyById,
	uploadFile
} from '../api/studiesAPI';
import AuthContext from '../store/auth-context';
import PreliminaryStudyDataForm from '../components/forms/PreliminaryStudyDataForm';
import WrapperModal from '../components/ui/WrapperModal';
import AddDocumentsForm from '../components/forms/AddDocumentsForm';
import ConfirmationWarning from '../components/ui/ConfirmationWarning';
import StateSelector from '../components/mainDashboard/StateSelector';
import DashboardStudyItem from '../components/mainDashboard/DashboardStudyItem';
import Pagination from '../components/mainDashboard/Pagination';
import Resultspages from '../components/mainDashboard/Resultpages';
import DashboardGreet from '../components/mainDashboard/DashboardGreet';
import DashboardHeader from '../components/mainDashboard/DashboardHeader';
import { getBlob } from '../store/blob-store';
import {
	getColorForStudyStatus,
	getColorForStudyStatus2
} from '../utils/varMappers';
import { CleanStudyProcess } from '../utils/cleanStudyProcess';
import { fileActions } from '../store/files-slice';
import PDFDocumentViewer from '../components/pdfViewer/PDFDocumentViewer';
import DiseaseTypeForm from '../components/forms/DiseaseTypeForm';
import { uiActions } from '../store/ui-slice';
import StepProgressBar from '../components/stepProgressBar/StepProgressBar';
import StudyQCTForm from '../components/forms/StudyQCTForm';
import { EditStudyContextProvider } from '../store/edit-study-context';
import Button from '../components/ui/Button';
import Loader from '../components/ui/Loader';

import './PIDashboard.scss';
import CreateAmendmentByGin from '../components/amendment/CreateAmendmentByGin';
import { AccessibilityContext } from '../store/accesibility-context';
import { useEffect } from 'react';

const studyCreationSteps = [
	{ label: 'Study data', id: 123 },
	{ label: 'Upload documents', id: 567 },
	{ label: 'Protocol details', id: 589 },
	{ label: 'QCT form', id: 987 }
];

const cardColorsQAM = {
	ONE: 1,
	TWO: 2,
	SUBMITTED_FOR_ANALYSIS: 3,
	STUDY_COMPLETED: 4,
	ARCHIVED: 4,
	REVISION_REQUESTED: 5
};
const cardColorPI = {
	ONE: 1,
	TWO: 2,
	SUBMITTED_FOR_ANALYSIS: 3,
	STUDY_COMPLETED: 4,
	ARCHIVED: 4,
	REVISION_REQUESTED: 5
};
const pdfTestName =
	'1695863834776_Protocol_document_test.pdf';

const DashboardPage = () => {
	const queryClient = useQueryClient();
	const authCtx = useContext(AuthContext);
	const roleUser = authCtx.userData.role;
	const accessibilityCtx = useContext(AccessibilityContext);
	const navigate = useNavigate();
	useEffect(() => {
		if (roleUser === 'SUPER') {
			navigate('/dashboard-admin');
		}
		if (roleUser === 'HOSPITAL') {
			navigate('/hospital-admin');
		}
		if (roleUser === 'SUPPORT') {
			navigate('/support-page');
		}
	}, []);

	const [pageNumber, setPageNumber] = useState(1);

	const [currentOption, setCurrentOption] =
		useState('none');
	const [searchParams] = useSearchParams();
	const q = searchParams.get('q');

	const { isLoading, error, data } = useQuery({
		queryKey: ['studies', pageNumber, currentOption, q],
		queryFn: () =>
			fetchStudies(pageNumber, currentOption, q),
		retry: false,
		staleTime: 3000000 //5 minutes to refetch
	});

	const [studyCreatedProtocol, setStudyCreatedProtocol] =
		useState();
	const [
		studyIdCreatedProtocol,
		setStudyIdCreatedProtocol
	] = useState();
	const [showAddStudy, setShowAddStudy] = useState(false);
	const [addStudyStep, setAddStudyStep] = useState('');
	const [newStudyData, setNewStudyData] = useState({});
	const [isAmendment, setIsAmendment] = useState(false); //change to useRef
	const [isAmendmentWarning, setIsAmendmentWarning] =
		useState(false);
	const [studyCreationStep, setStudyCreationStep] =
		useState(0);
	const [createdByGin, setCreatedByGin] = useState(false);
	const [amendmentFromId, setAmendmentFromId] =
		useState(null);
	const [
		informedConsentEntered,
		setInformedConsentEntered
	] = useState(false);

	const isStudyCreationBarActive =
		showAddStudy || studyCreatedProtocol || addStudyStep;

	const fileSlice = useSelector(
		(state) => state.filesManager.files
	);

	const dispatch = useDispatch();

	const { data: amendmentData } = useQuery({
		queryKey: ['amendmentFrom', amendmentFromId],
		queryFn: () => getStudyById(amendmentFromId),
		retry: false,
		staleTime: 3000000, //5 minutes to refetch
		enabled: !!amendmentFromId && !!data
	});

	const stateRevisionTitleQAM = {
		UNCOMPLETED: 'Missing Protocol Information',
		ONE: 'Ready For Review',
		TWO: 'Under P.I. Review',
		STUDY_COMPLETED: 'Completed',
		ARCHIVED: 'Archived',
		REVISION_REQUESTED: 'Revision Requested',
		SUBMITTED_FOR_ANALYSIS: 'Submitted For Analysis',
		PENDING_APPROVAL: 'Pending QCT Approval'
	};
	const stateRevisionTitlePI = {
		UNCOMPLETED: 'Missing Protocol Information',
		ONE: 'Under Q.A.M. Review',
		TWO: 'Ready For Review',
		STUDY_COMPLETED: 'Completed',
		ARCHIVED: 'Archived',
		REVISION_REQUESTED: 'Revision Requested',
		SUBMITTED_FOR_ANALYSIS: 'Submitted For Analysis',
		PENDING_APPROVAL: 'Pending QCT Approval'
	};

	const onPageChange = (targetPageNumber) => {
		setPageNumber(targetPageNumber);
		queryClient.invalidateQueries('studies');
	};

	const addStudyHandler = (amending) => {
		setIsAmendmentWarning(false);
		setCreatedByGin(false);
		if (amending === true) {
			setIsAmendment(true);
		} else {
			setIsAmendment(false);
		}
		setShowAddStudy(true);
		setAddStudyStep('PRELIMINARY_DATA');
		setStudyCreationStep(0);
	};

	const addAmendmentByGin = () => {
		setIsAmendmentWarning(false);
		setCreatedByGin(true);
	};

	const cancelStudyCreationHandler = () => {
		setShowAddStudy(false);
		setAddStudyStep('');
		setStudyCreationStep(0);
		setIsAmendment(false);
		setIsAmendmentWarning(false);
		setCreatedByGin(false);
		setInformedConsentEntered(false);
	};

	const startDocsSelection = (preliminaryData) => {
		setNewStudyData(preliminaryData);
		setAddStudyStep('UPLOAD_DOCS');
		setStudyCreationStep(1);
	};

	const startQCTconfirmationForm = () => {
		setAddStudyStep('LAUNCH_STUDY_INFO_FORM');
		setStudyCreationStep(3);
	};

	const qctSent = () => {
		setAddStudyStep('');
		queryClient.invalidateQueries('studies');
	};

	const reviewStudyCreation = () => {
		setAddStudyStep('UPLOAD_DOCS');
	};

	const confirmStudyCreation = () => {
		setAddStudyStep('CONFIRM_STUDY_CREATION');
	};

	const uploadFileListToBucket = (fileList) => {
		const fileListUploadPromises = fileList.map((file) => {
			const currentBlob = getBlob(file.name);
			return uploadFile(file.signedPutUrl, currentBlob);
		});
		return Promise.all(fileListUploadPromises);
	};

	const handleStudyCreationError = (studyType) => {
		dispatch(
			uiActions.launchNotification({
				title: `The ${studyType} could not be created`,
				message:
					'An unexpected error has ocurred, please try again!',
				status: 'error'
			})
		);
	};

	const createStudy = () => {
		setStudyCreatedProtocol('waiting');
		setStudyCreationStep(2);
		setShowAddStudy(false);
		setAddStudyStep('');

		if (isAmendment) {
			const uploadFilesPromise =
				uploadFileListToBucket(fileSlice);

			uploadFilesPromise
				.then((result) => {
					console.log(result);
					const fileStudyDataList = fileSlice.map(
						(file) => {
							return {
								fileObject: file.name,
								fileType: file.documentType
							};
						}
					);
					addAmendment({
						title: newStudyData.title,
						sponsor: newStudyData.sponsor,
						fundingType: newStudyData.fundingType,
						phaseOfStudy: newStudyData.phaseOfStudy,
						department: newStudyData.deparment,
						nationalClinicalTrial:
							newStudyData.nationalClinicalTrial,
						files: fileStudyDataList,
						pi: newStudyData.pi,
						informedConsentFormDate:
							newStudyData.informedConsentFormDate,
						IRBApprovalDate: newStudyData.irbApprobalDate,
						CRFOBudgetReviewCompletedDate:
							newStudyData.crfoBudgetReviewCompletedDate,
						clinicalTrialAgreementVersion:
							newStudyData.clinicalTrialAgreementVersion,
						nameOfInvestigationalDrug:
							newStudyData.nameOfInvestigationalDrug,
						nameOfInvestigationalDevice:
							newStudyData.nameOfInvestigationalDevice,
						IDN: newStudyData.IDN,
						IDE: newStudyData.IDE,
						longTitle: newStudyData.longTitle,
						protocolDate: newStudyData.protocolDate,
						oldStudy: amendmentFromId
					})
						.then((result) => {
							setStudyCreatedProtocol(
								result.data.studyDB.protocol[0]
							);
							setStudyIdCreatedProtocol(
								result.data.studyDB._id
							);
						})
						.catch(() =>
							handleStudyCreationError('Amendment')
						);
					queryClient.invalidateQueries('studies');
				})
				.catch((err) => {
					// TODO handle upload files error
					console.log(err);
					console.log(err.config.data.name);
					handleStudyCreationError('Amendment');
				});
			setAmendmentFromId(null);
		} else {
			const uploadFilesPromise =
				uploadFileListToBucket(fileSlice);

			uploadFilesPromise
				.then((result) => {
					console.log(result);
					const fileStudyDataList = fileSlice.map(
						(file) => {
							return {
								fileObject: file.name,
								fileType: file.documentType
							};
						}
					);
					addStudy({
						title: newStudyData.title,
						sponsor: newStudyData.sponsor,
						fundingType: newStudyData.fundingType,
						phaseOfStudy: newStudyData.phaseOfStudy,
						department: newStudyData.deparment,
						nationalClinicalTrial:
							newStudyData.nationalClinicalTrial,
						files: fileStudyDataList,
						pi: newStudyData.pi,
						informedConsentFormDate:
							newStudyData.informedConsentFormDate,
						IRBApprovalDate: newStudyData.irbApprobalDate,
						CRFOBudgetReviewCompletedDate:
							newStudyData.crfoBudgetReviewCompletedDate,
						clinicalTrialAgreementVersion:
							newStudyData.clinicalTrialAgreementVersion,
						nameOfInvestigationalDrug:
							newStudyData.nameOfInvestigationalDrug,
						nameOfInvestigationalDevice:
							newStudyData.nameOfInvestigationalDevice,
						IDN: newStudyData.IDN,
						IDE: newStudyData.IDE,
						longTitle: newStudyData.longTitle,
						protocolDate: newStudyData.protocolDate
					})
						.then((result) => {
							setStudyCreatedProtocol(
								result.data.studyDB.protocol[0]
							);
							setStudyIdCreatedProtocol(
								result.data.studyDB._id
							);
						})
						.catch(() => handleStudyCreationError('Study'));

					queryClient.invalidateQueries('studies');
				})
				.catch((err) => {
					// TODO handle upload files error
					console.log(err);
					console.log(err.config.data.name);
					handleStudyCreationError('Study');
				});
		}

		CleanStudyProcess();
		dispatch(fileActions.removeAllFiles());
		//TODO add addAmendment endpoint when isAmendment is true
	};

	const cancelPdfViewerProcess = () => {
		setStudyCreatedProtocol();
	};

	const cancelQCTform = () => {
		setAddStudyStep('');
	};

	let preliminaryStudyTitle = 'Create New Study';
	let addDocumentsFormTitle = 'Add New Study';
	let confirmationWarningTitle = 'Create New Study';
	let confirmationWarningSubtitle =
		'You’re going to create a New Study. Once is created, it will be processed.';

	if (isAmendment) {
		preliminaryStudyTitle = 'Create New Amendment';
		addDocumentsFormTitle = 'Add New Amendment';
		confirmationWarningTitle = 'Create New Amendment';
		confirmationWarningSubtitle =
			'You’re going to create a New Amendment. Once is created, it will be processed.';
	}

	if (isLoading) {
		return <Loader centered />;
	}

	const { studies, totalStudies, itemsPerPage } = data.data;

	return (
		<div className='dashboard-general-wrapper'>
			<DashboardHeader />
			<DashboardGreet />
			{/* TODO create a DashboardContent component and migrate all grid elements, controls an pagination there */}
			<div className='dashboard-content-container'>
				<div className='content-header'>
					<div className='content-title'>
						<h2>Your Coverage Analysis</h2>
					</div>
					<div className='content-controls'>
						<StateSelector
							currentOption={currentOption}
							onStateSelected={setCurrentOption}
							role={
								authCtx.userData.role === 'QA'
									? 'Under P.I. Review'
									: 'Under Q.A.M. Review'
							}
						/>
						{authCtx.userData.role === 'QA' && (
							<>
								<Button
									onClick={() => addStudyHandler(false)}
									className={`${
										accessibilityCtx.contrast === false
											? 'primary-button'
											: 'primary-button-contrast'
									} 'create-study-buttons new-study-btn'`}
									startIcon='add'
								>
									New Study
								</Button>
								<Button
									onClick={() =>
										setIsAmendmentWarning(true)
									}
									className='secondary-button create-study-buttons new-amend-btn'
									startIcon='add'
								>
									New Amendment
								</Button>
							</>
						)}
					</div>
				</div>
				<div className='studies-list-container'>
					{/* TODO filtering should be done by the backend */}
					{studies.map((item, index) => (
						<DashboardStudyItem
							key={`${index}${
								authCtx.userData.role === 'QA'
									? (item.pi?.id ?? 'N/A')
									: (item.qam?.id ?? 'N/A')
							}`}
							id={item._id}
							// TODO color should come from the backend as a string
							color={
								accessibilityCtx.contrast === false
									? getColorForStudyStatus(item.status)
									: getColorForStudyStatus2(item.status)
							}
							title={item.title}
							status={
								authCtx.userData.role === 'QA'
									? stateRevisionTitleQAM[item.status]
									: stateRevisionTitlePI[item.status]
							}
							type={item.type}
							sponsor={item.sponsor}
							role={
								authCtx.userData.role === 'QA'
									? 'P.I.'
									: 'Q.A.M.'
							}
							username={
								authCtx.userData.role === 'QA'
									? (item.pi?.firstname ?? 'N/A')
									: (item.qam?.firstname ?? 'N/A')
							}
							docUpload={item.documentsUploaded}
							warning={item.issue}
						/>
					))}
				</div>
				{/* TODO Paginator should be a single component */}
				<div className='pagination'>
					<Resultspages
						currentPage={pageNumber}
						pageSize={itemsPerPage}
						totalElements={totalStudies}
					/>

					<Pagination
						currentPage={pageNumber}
						sizeNumber={itemsPerPage}
						totalNumber={totalStudies}
						changePage={onPageChange}
					/>
				</div>
			</div>
			{isStudyCreationBarActive && (
				<StepProgressBar
					steps={studyCreationSteps}
					currentStep={studyCreationStep}
				/>
			)}
			{showAddStudy && (
				<WrapperModal
					onDismiss={cancelStudyCreationHandler}
				>
					<EditStudyContextProvider
						initialState={amendmentData?.data?.study}
					>
						{addStudyStep === 'PRELIMINARY_DATA' && (
							<PreliminaryStudyDataForm
								title={preliminaryStudyTitle}
								onDocsSelection={startDocsSelection}
								onCancel={cancelStudyCreationHandler}
								informedConsentEntered={
									setInformedConsentEntered
								}
							/>
						)}
						{addStudyStep === 'UPLOAD_DOCS' && (
							<AddDocumentsForm
								docsConfirmLabel={addDocumentsFormTitle}
								onDocsConfirm={confirmStudyCreation}
								onBack={() => addStudyHandler(isAmendment)}
								informedConsentEntered={
									informedConsentEntered
								}
							/>
						)}
						{addStudyStep === 'CONFIRM_STUDY_CREATION' && (
							<ConfirmationWarning
								title={confirmationWarningTitle}
								subtitle={confirmationWarningSubtitle}
								message='Are you sure you uploaded the latest version of approved documents?'
								acceptLabel='Yes, create'
								declineLabel='No, review'
								onAccept={createStudy}
								onDecline={reviewStudyCreation}
							/>
						)}
					</EditStudyContextProvider>
				</WrapperModal>
			)}
			{studyCreatedProtocol && (
				<WrapperModal onDismiss={cancelPdfViewerProcess}>
					<DiseaseTypeForm
						studyId={studyIdCreatedProtocol}
						fileName={studyCreatedProtocol}
						onEndProcess={setStudyCreatedProtocol}
						onQCTform={startQCTconfirmationForm}
					/>
					<PDFDocumentViewer
						documentName={studyCreatedProtocol}
					/>
				</WrapperModal>
			)}
			{addStudyStep === 'LAUNCH_STUDY_INFO_FORM' && (
				<WrapperModal onDismiss={cancelQCTform}>
					<StudyQCTForm
						studyId={studyIdCreatedProtocol}
						onSendQCT={qctSent}
					/>
				</WrapperModal>
			)}
			{isAmendmentWarning && (
				<WrapperModal
					onDismiss={cancelStudyCreationHandler}
				>
					<ConfirmationWarning
						title='Create New Amendment'
						subtitle=''
						message='Select Yes if this amendment is based on an existing study in Ginseng. Select No if it is for a study not yet created in the platform.'
						acceptLabel='Yes'
						declineLabel='No'
						onAccept={addAmendmentByGin}
						onDecline={() => addStudyHandler(true)}
					/>
				</WrapperModal>
			)}
			{createdByGin && (
				<WrapperModal
					onDismiss={cancelStudyCreationHandler}
					defaultWidth='1000px'
				>
					<CreateAmendmentByGin
						onAddAmendmentFrom={setAmendmentFromId}
						onStartAmendmentFromStudy={() =>
							addStudyHandler(true)
						}
					/>
				</WrapperModal>
			)}
		</div>
	);
};

export default DashboardPage;
